import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, InputAdornment, CircularProgress, IconButton, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, FormControl, Alert } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { getBots, deleteBot, createBot, updateBot } from "../api/adminApi";
const BASE = (process.env.REACT_APP_AUTH_API || "http://localhost:5000/api/auth").replace("/api/auth", "");

const Bots = () => {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [confirm, setConfirm] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [editingBot, setEditingBot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", userId: "", status: "active" });

  const fetchBots = () => {
    setLoading(true);
    getBots({ search }).then((data) => setBots(data.bots || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchBots(); }, [search]);

  const openCreate = () => {
    setForm({ name: "", userId: "", status: "active" });
    setEditingBot(null);
    setDialog(true);
    fetch(`${BASE}/api/admin/users`).then(r => r.json()).then(d => setUsers(d.users || [])).catch(() => {});
  };

  const openEdit = (bot) => {
    setForm({ name: bot.name, userId: bot.owner?.id || "", status: bot.status });
    setEditingBot(bot._id);
    setDialog(true);
    fetch(`${BASE}/api/admin/users`).then(r => r.json()).then(d => setUsers(d.users || [])).catch(() => {});
  };

  const handleSave = async () => {
    if (!form.name || !form.userId) return;
    setSaving(true);
    try {
      if (editingBot) {
        const data = await updateBot(editingBot, { name: form.name, status: form.status });
        setSnack({ open: true, message: "Bot updated", severity: "success" });
      } else {
        const data = await createBot({ name: form.name, userId: form.userId });
        setSnack({ open: true, message: "Bot created", severity: "success" });
      }
      setDialog(false);
      fetchBots();
    } catch { setSnack({ open: true, message: "Failed to save bot", severity: "error" }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await deleteBot(confirm);
      setSnack({ open: true, message: "Bot deleted", severity: "success" });
      setConfirm(null);
      fetchBots();
    } catch { setSnack({ open: true, message: "Failed to delete bot", severity: "error" }); }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
        <Box>
          <Typography fontWeight={800} fontSize={24} color="#1e293b">Bots</Typography>
          <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>{bots.length} bots found</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
            Create Bot
          </Button>
          <TextField size="small" placeholder="Search bots..." value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#64748b" }} /></InputAdornment>, sx: { bgcolor: "#ffffff", borderRadius: 2, color: "#1e293b", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } } }}
            sx={{ width: 240 }} />
        </Box>
      </Box>

      <Paper className="glass-card" sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Bot Name</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Owner</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Status</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }}>Created</TableCell>
                <TableCell sx={{ color: "#64748b", fontWeight: 700, fontSize: 12, textTransform: "uppercase", borderColor: "#e2e8f0" }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, borderColor: "#e2e8f0" }}><CircularProgress /></TableCell></TableRow>
              ) : bots.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "#64748b", borderColor: "#e2e8f0" }}>No bots found</TableCell></TableRow>
              ) : bots.map((bot) => (
                <TableRow key={bot._id} hover sx={{ "&:hover": { bgcolor: "#f8faff" }, "&:last-child td": { border: 0 } }}>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}><Typography fontWeight={600} fontSize={14} color="#1e293b">{bot.name}</Typography></TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}>
                    <Typography variant="body2" color="#475569">{bot.owner?.name || "Unknown"}</Typography>
                    <Typography variant="caption" color="#64748b">{bot.owner?.email || ""}</Typography>
                  </TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: 1, bgcolor: bot.status === "active" ? "#10b98112" : "#64748b12" }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: bot.status === "active" ? "#10b981" : "#64748b" }} />
                      <Typography variant="caption" fontWeight={600} color={bot.status === "active" ? "#10b981" : "#64748b"} sx={{ textTransform: "capitalize" }}>{bot.status}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }}><Typography variant="body2" color="#475569">{new Date(bot.createdAt).toLocaleDateString()}</Typography></TableCell>
                  <TableCell sx={{ borderColor: "#e2e8f0" }} align="right">
                    <IconButton size="small" onClick={() => openEdit(bot)} sx={{ color: "#6366f1", "&:hover": { bgcolor: "#6366f112" } }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setConfirm(bot._id)} sx={{ color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ className: "glass-card", sx: { borderRadius: "16px !important" } }}>
        <DialogTitle sx={{ color: "#1e293b", fontWeight: 700 }}>{editingBot ? "Edit Bot" : "Create Bot"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField label="Bot Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required size="small"
              InputProps={{ sx: { bgcolor: "#ffffff", borderRadius: 2, color: "#1e293b", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } } }}
              InputLabelProps={{ sx: { color: "#64748b" } }} />
            <FormControl size="small" required>
              <Typography variant="caption" color="#64748b" mb={0.5}>Owner</Typography>
              <Select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })}
                sx={{ bgcolor: "#ffffff", borderRadius: 2, color: "#1e293b", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" }, "& .MuiSvgIcon-root": { color: "#64748b" } }}>
                {users.map((u) => (
                  <MenuItem key={u._id} value={u._id} sx={{ fontSize: 13 }}>{u.fullName} ({u.email})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <Typography variant="caption" color="#64748b" mb={0.5}>Status</Typography>
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                sx={{ bgcolor: "#ffffff", borderRadius: 2, color: "#1e293b", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" }, "& .MuiSvgIcon-root": { color: "#64748b" } }}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialog(false)} sx={{ color: "#64748b", textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !form.name || !form.userId}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
            {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : editingBot ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} PaperProps={{ className: "glass-card", sx: { borderRadius: "16px !important" } }}>
        <DialogTitle sx={{ color: "#1e293b", fontWeight: 700 }}>Delete Bot</DialogTitle>
        <DialogContent><Typography color="#475569">Are you sure? This action cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setConfirm(null)} sx={{ color: "#64748b", textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" sx={{ textTransform: "none", borderRadius: 2, bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} sx={{ borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Bots;
